import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wishlist, type WishlistItem } from '@/lib/api/v1';
import { Badge, Button, Card, EmptyState, Icon, Skeleton } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { useToasts } from '@/lib/store';
import { formatPriceNok } from '@/lib/format';

/**
 * Wishlist — catalog products the user wants but doesn't own yet.
 * Sourced from Nyheter, bottle-detail catalog view, or anywhere a user
 * sees a catalog product. Separate from the cellar (which holds owned
 * bottles) but shares the same product taxonomy via catalog productId.
 */
export function WishlistPage() {
  const qc = useQueryClient();
  const { push } = useToasts();

  const wishlistQ = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => Wishlist.list(),
  });

  const remove = useMutation({
    mutationFn: (productId: string) => Wishlist.remove(productId),
    onSuccess: () => {
      push({ title: 'Fjernet fra ønskelisten', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['wishlist'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke fjerne', description: err.message, tone: 'danger' }),
  });

  const items = wishlistQ.data ?? [];

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Ønskeliste</h1>
        <p className="text-sm text-mist">
          Vin du har lyst på men ikke har kjøpt enda. {items.length > 0 && (
            <span className="text-candle">{items.length} {items.length === 1 ? 'flaske' : 'flasker'}</span>
          )}
        </p>
      </header>

      {wishlistQ.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-4">
              <Skeleton height={128} rounded="md" />
              <Skeleton height={18} width="80%" />
              <Skeleton height={12} width="60%" />
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="heart"
          title="Ønskelisten er tom"
          description="Legg til vin du har lyst på fra Nyheter eller når du finner noe i katalogen."
          action={
            <Link to="/nyheter" className="ui-btn ui-btn--primary">
              <Icon name="sparkles" size={14} /> Utforsk nyheter
            </Link>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <WishlistCard
              key={item.id}
              item={item}
              onRemove={() => remove.mutate(item.productId)}
              removing={remove.isPending && remove.variables === item.productId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WishlistCard({
  item,
  onRemove,
  removing,
}: {
  item: WishlistItem;
  onRemove: () => void;
  removing: boolean;
}) {
  const image =
    item.imageUrl ??
    `https://bilder.vinmonopolet.no/cache/300x300-0/${item.productId}-1.jpg`;
  return (
    <div className="ui-card grid gap-3 p-4">
      <Link to={`/bottle/${item.productId}`} className="grid gap-3">
        <div className="relative grid h-32 place-items-center overflow-hidden rounded-md bg-ink-soft sm:h-36 lg:h-32 xl:h-36">
          <BottleImage src={image} alt={item.name} iconSize={40} mode="lightbox" />
          {item.priceNok != null && (
            <span className="absolute bottom-2 right-2 ui-badge ui-badge--gold">
              {formatPriceNok(item.priceNok)}
            </span>
          )}
        </div>
        <div className="grid gap-1">
          <h3 className="font-serif text-base leading-tight text-parchment line-clamp-2">{item.name}</h3>
          <div className="flex items-center gap-2 text-xs text-mist">
            {[item.region, item.country, item.vintage].filter(Boolean).join(' · ')}
          </div>
          {item.note && <p className="text-xs italic text-parchment-dim line-clamp-2">"{item.note}"</p>}
        </div>
      </Link>
      <div className="flex items-center justify-between gap-2 pt-1">
        <Badge tone="mist">#{item.productId}</Badge>
        <Button
          size="sm"
          variant="ghost"
          iconLeft="trash"
          loading={removing}
          onClick={onRemove}
        >
          Fjern
        </Button>
      </div>
    </div>
  );
}
